/**
 * Web twins of the composer preset affordances. Same contract and copy as
 * `CvPresetControls.tsx`; RTL is handled with logical properties rather than a
 * mirrored flexDirection so the chip reads correctly in Arabic.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, primaryShades } from '@tabbio/tokens';
import { X } from 'lucide-react';
import { NovaSparkle } from '../common/NovaSparkle';
import { useTheme } from '../../hooks/useTheme';

export function CvPresetPill({
  onPress,
  isActive,
  disabled = false,
}: {
  onPress: () => void;
  isActive: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation('auth');
  const [hovered, setHovered] = useState(false);
  const label = t('onboarding.cvPreset.triggerLabel', {
    defaultValue: 'Custom CV',
  });

  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      aria-pressed={isActive}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...pillStyle,
        borderColor: isActive ? colors.primary : 'var(--input-border)',
        backgroundColor:
          hovered && !disabled ? 'rgba(94,41,255,0.06)' : 'transparent',
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <NovaSparkle size={14} />
      <span style={{ color: colors.primary, fontSize: 13, fontWeight: 500 }}>
        {label}
      </span>
    </button>
  );
}

export function CvPresetAppliedChip({
  presetLabel,
  onChange,
  onRemove,
  disabled = false,
}: {
  presetLabel: string;
  onChange: () => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('auth');
  const { isDark } = useTheme();
  const changeLabel = t('onboarding.cvPreset.changeLabel', {
    defaultValue: 'Change',
  });
  const removeLabel = t('onboarding.cvPreset.removeLabel', {
    defaultValue: 'Remove custom CV',
  });

  return (
    <div
      style={{
        ...chipStyle,
        backgroundColor: isDark ? 'rgba(94,41,255,0.18)' : primaryShades[50],
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <NovaSparkle size={14} style={{ flexShrink: 0 }} />
      <span dir="auto" style={chipLabelStyle}>
        {t('onboarding.cvPreset.appliedLabel', {
          preset: presetLabel,
          defaultValue: '{{preset}} applied',
        })}
      </span>
      <button
        type="button"
        onClick={onChange}
        disabled={disabled}
        style={chipActionStyle}
      >
        {changeLabel}
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={removeLabel}
        style={{ ...chipActionStyle, display: 'inline-flex', padding: 0 }}
      >
        <X size={14} strokeWidth={2} color={colors.primary} />
      </button>
    </div>
  );
}

const pillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  height: 32,
  paddingInline: 12,
  borderRadius: 9999,
  borderWidth: 1,
  borderStyle: 'solid',
  fontFamily: 'inherit',
  flexShrink: 0,
  transition: 'background-color 150ms, border-color 150ms',
};

const chipStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  paddingInline: 12,
  paddingBlock: 8,
  borderRadius: 12,
  boxSizing: 'border-box',
};

const chipLabelStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 500,
  color: colors.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const chipActionStyle: CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 500,
  color: colors.primary,
  cursor: 'pointer',
  flexShrink: 0,
  alignItems: 'center',
};
